import { getExtensionValue } from "@medplum/core";
import { QuestionnaireItem, QuestionnaireResponseItem } from "@medplum/fhirtypes";

import { Checkbox, Stack, TitleOrder } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { TextWrapper } from "@/components/TextWrapper";
import { Extensions } from "@/fhir/enums/extensions";
import { INTAKE_FORMS_FIELDS } from "@/intake-forms/constants/intake-forms";
import { EnableWhenBehavior } from "@/intake-forms/types/intake-forms";

type QuestionnaireCheckboxField<T> = {
  label?: string;
  name: string;
  group: QuestionnaireItem | undefined;
  parentGroup?: QuestionnaireItem | undefined;
  form: UseFormReturnType<T>;
  parentPath?: string;
  questionnaireResponse?: QuestionnaireResponseItem;
  groupTitleOrder?: TitleOrder;
  canShowControlledFormset?: boolean;
};

const QuestionnaireCheckboxField = <T,>({
  group,
  parentGroup,
  form,
  label,
  name,
  parentPath,
  questionnaireResponse,
  groupTitleOrder,
  canShowControlledFormset,
}: QuestionnaireCheckboxField<T>) => {
  const { checked, ...formInputProps } = form.getInputProps(name, { type: "checkbox" });

  // Check if this group should use inline enableWhen behavior
  const useInlineEnableWhen = getExtensionValue(
    group,
    Extensions.QuestionnaireItemInlineEnableWhen,
  ) as boolean;

  if (!useInlineEnableWhen) {
    // Original behavior - render all checkboxes together
    return (
      <Checkbox.Group label={label} value={checked || []} {...formInputProps}>
        <Stack>
          {group?.answerOption?.map((item) => (
            <Checkbox key={item.valueString} value={item.valueString} label={item.valueString} />
          ))}
        </Stack>
      </Checkbox.Group>
    );
  }

  // New inline behavior - render each checkbox with its enableWhen fields directly below
  return (
    <Stack gap="xs">
      {label && <TextWrapper variant="label">{label}</TextWrapper>}
      {group?.answerOption?.map((option) => {
        const optionValue = option.valueString;
        const isChecked = checked?.includes(optionValue);

        // Find all items that have enableWhen conditions based on this checkbox
        // In a formset context, the enableWhen fields are siblings within the same formset item
        // Use the linkId for comparison since enableWhen.question refers to linkId, not the full name with parent path
        const checkboxLinkId = name.split(".").pop() || name;
        const relatedEnableWhenItems =
          parentGroup?.item?.filter((item) =>
            item.enableWhen?.some(
              (enableWhen) =>
                enableWhen.question === checkboxLinkId &&
                enableWhen.operator === "=" &&
                enableWhen.answerString === optionValue,
            ),
          ) || [];

        return (
          <Stack gap="xs" key={optionValue}>
            <Checkbox
              value={optionValue}
              label={optionValue}
              checked={isChecked}
              onChange={(event) => {
                const newChecked = event.currentTarget.checked
                  ? [...(checked || []), optionValue]
                  : (checked || []).filter((v: string) => v !== optionValue);
                form.setFieldValue(name, newChecked);
              }}
            />

            {/* Render enableWhen fields directly below this checkbox if it's checked */}
            {isChecked &&
              relatedEnableWhenItems.map((item) => {
                const component = INTAKE_FORMS_FIELDS[item.type];
                if (!component) return null;

                const formFieldName = parentPath ? `${parentPath}.${item.linkId}` : item.linkId;

                return (
                  <div key={item.linkId} style={{ marginLeft: "24px", marginTop: "8px" }}>
                    {component({
                      group: parentGroup,
                      key: item.linkId,
                      form,
                      formFieldName,
                      label: item.text || "",
                      name: item.linkId,
                      required: item.required,
                      questionnaireItem: item,
                      questionnaireResponseItem: questionnaireResponse?.item?.filter(
                        (response: QuestionnaireResponseItem) => response.linkId === item.linkId,
                      )[0],
                      enableWhen: item?.enableWhen,
                      enableWhenBehavior: item?.enableBehavior as EnableWhenBehavior | undefined,
                      rightSection:
                        (getExtensionValue(
                          item,
                          Extensions.QuestionnaireFieldRightSection,
                        ) as string) || "",
                      placeholder:
                        (getExtensionValue(
                          item,
                          Extensions.QuestionnaireFieldPlaceholder,
                        ) as string) || "",
                      icon:
                        (getExtensionValue(item, Extensions.QuestionnaireFieldIcon) as string) ||
                        "",
                      mask:
                        (getExtensionValue(item, Extensions.QuestionnaireFieldMask) as string) ||
                        "",
                      minRange: getExtensionValue(item, Extensions.QuestionnaireItemMinRange) as
                        | number
                        | undefined,
                      maxRange: getExtensionValue(item, Extensions.QuestionnaireItemMaxRange) as
                        | number
                        | undefined,
                      allowDecimal: getExtensionValue(
                        item,
                        Extensions.QuestionnaireItemAllowDecimal,
                      ) as boolean | undefined,
                      autoPopulate: getExtensionValue(
                        item,
                        Extensions.QuestionnaireFieldAutoPopulate,
                      ) as boolean | undefined,
                      itemOrder: getExtensionValue(item, Extensions.QuestionnaireItemOrder) as
                        | number
                        | undefined,
                      groupTitleOrder,
                    })}
                  </div>
                );
              })}
          </Stack>
        );
      })}
    </Stack>
  );
};

export default QuestionnaireCheckboxField;
